import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentModal = ({ isOpen, onClose, invoice, onSubmit }) => {
  if (!isOpen) return null;

  const [formData, setFormData] = useState({
    invoiceId: invoice?.id,
    customerId: invoice?.customer_id,
    amount: invoice?.amount_due || 0,
    paymentMethod: 'Credit Card',
    paymentDate: new Date()?.toISOString()?.split('T')?.[0],
    transactionId: '',
    notes: ''
  });

  const paymentMethods = [
    { value: 'Credit Card', label: 'Credit Card' },
    { value: 'Debit Card', label: 'Debit Card' },
    { value: 'Bank Transfer', label: 'Bank Transfer' },
    { value: 'Check', label: 'Check' },
    { value: 'Cash', label: 'Cash' },
    { value: 'PayPal', label: 'PayPal' },
    { value: 'Other', label: 'Other' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (formData?.amount <= 0) {
      alert('Payment amount must be greater than zero');
      return;
    }

    if (formData?.amount > invoice?.amount_due) {
      const confirm = window.confirm(
        `Payment amount ($${formData?.amount}) exceeds amount due ($${invoice?.amount_due}). Continue?`
      );
      if (!confirm) return;
    }

    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl">
        <div className="bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Record Payment</h2>
              <p className="text-sm text-muted-foreground">Invoice: {invoice?.invoice_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Invoice Summary */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Customer:</span>
              <span className="font-medium text-foreground">{invoice?.customer?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Amount:</span>
              <span className="font-medium text-foreground">${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount Paid:</span>
              <span className="font-medium text-success">${parseFloat(invoice?.amount_paid || 0)?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
              <span className="text-foreground">Amount Due:</span>
              <span className="text-error">${parseFloat(invoice?.amount_due || 0)?.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              label="Payment Amount"
              placeholder="0.00"
              required
              min="0.01"
              step="0.01"
              value={formData?.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e?.target?.value) || 0 }))}
            />
            <Select
              label="Payment Method"
              required
              options={paymentMethods}
              value={formData?.paymentMethod}
              onChange={(value) => setFormData(prev => ({ ...prev, paymentMethod: value }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Payment Date"
              required
              value={formData?.paymentDate}
              onChange={(e) => setFormData(prev => ({ ...prev, paymentDate: e?.target?.value }))}
            />
            <Input
              label="Transaction ID (Optional)"
              placeholder="e.g., TXN-123456"
              value={formData?.transactionId}
              onChange={(e) => setFormData(prev => ({ ...prev, transactionId: e?.target?.value }))}
            />
          </div>

          <Input
            label="Notes (Optional)"
            placeholder="Additional payment notes"
            value={formData?.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" iconName="Check" iconPosition="left">
              Record Payment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;